import { useState, useEffect, useCallback } from 'react';
import { payrollAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import PageHeader from '@/components/Layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Lock, LockOpen, Plus, DownloadSimple, Warning, CheckCircle,
  ArrowsClockwise, Play, Users, CalendarCheck, UserPlus,
  Money, Receipt, Eye, CaretRight, Trash, PencilSimple
} from '@phosphor-icons/react';

const fmt = (v) => formatCurrency(v || 0);
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// ─── Step-wise Payroll Wizard ────────────────────────────────────────────────
function PayrollWizard({ month, onComplete }) {
  const [runStatus, setRunStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [payrollData, setPayrollData] = useState(null);
  const [activeStep, setActiveStep] = useState(null);
  const [editDialog, setEditDialog] = useState(null);
  const [editValues, setEditValues] = useState({});

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await payrollAPI.getRunStatus(month);
      setRunStatus(res.data);
      // Find first incomplete step
      const firstIncomplete = res.data.steps.find(s => !s.done);
      if (firstIncomplete && !activeStep) setActiveStep(firstIncomplete.key);
    } catch { toast.error('Failed to load run status'); }
    finally { setLoading(false); }
  }, [month]);

  const loadPayroll = useCallback(async () => {
    try {
      const res = await payrollAPI.getMonthly(month);
      setPayrollData(res.data);
    } catch { /* no data yet is fine */ }
  }, [month]);

  useEffect(() => { loadStatus(); loadPayroll(); }, [loadStatus, loadPayroll]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await payrollAPI.generatePayroll(month);
      toast.success(res.data.message);
      loadStatus();
      loadPayroll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate payroll');
    } finally { setGenerating(false); }
  };

  const handleUploadExcel = async (file) => {
    if (!file) return;
    setUploadingExcel(true);
    try {
      const res = await payrollAPI.uploadPayrollExcel(month, file);
      toast.success(res.data.message || 'Payroll uploaded');
      loadStatus();
      loadPayroll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload payroll Excel');
    } finally { setUploadingExcel(false); }
  };

  const handleStepComplete = async (stepKey) => {
    const stepMap = {
      attendance: 'attendance_done',
      joinees_exits: 'joinees_exits_done',
      bonus_revisions: 'bonus_revisions_done',
      reimbursements: 'reimbursements_done',
    };
    try {
      await payrollAPI.markStepComplete(month, stepMap[stepKey]);
      toast.success('Step marked complete');
      loadStatus();
    } catch (e) { toast.error('Failed'); }
  };

  const handleLock = async () => {
    try {
      await payrollAPI.lockPayroll(month);
      toast.success('Payroll locked');
      loadStatus();
      onComplete?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleRemoveEmployee = async (empNo) => {
    if (!window.confirm(`Remove ${empNo} from this payroll?`)) return;
    try {
      await payrollAPI.removeEmployee(month, empNo);
      toast.success(`Removed ${empNo}`);
      loadPayroll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleEditSave = async () => {
    if (!editDialog) return;
    try {
      const updates = {};
      for (const [k, v] of Object.entries(editValues)) {
        if (k === 'payable_units') {
          updates[k] = v;
        } else {
          updates[k] = parseFloat(v) || 0;
        }
      }
      await payrollAPI.updateEmployeePayroll({
        month, employee_number: editDialog.employee_number, updates
      });
      toast.success('Updated');
      setEditDialog(null);
      loadPayroll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openEdit = (emp) => {
    if (activeStep === 'attendance') {
      setEditValues({ payable_units: emp.payable_units || '' });
    } else if (activeStep === 'bonus_revisions') {
      setEditValues({
        basic: emp.basic || 0, special_allowance: emp.special_allowance || 0,
        overtime: emp.overtime || 0, performance_incentives: emp.performance_incentives || 0,
      });
    } else if (activeStep === 'reimbursements') {
      setEditValues({
        food_coupons: emp.food_coupons || 0, travel_reimbursement: emp.travel_reimbursement || 0,
        fuel_reimbursement: emp.fuel_reimbursement || 0,
        pf_employee: emp.pf_employee || 0, esi_employee: emp.esi_employee || 0,
        income_tax: emp.income_tax || 0, professional_tax: emp.professional_tax || 0,
      });
    } else {
      setEditValues({
        basic: emp.basic || 0, hra: emp.hra || 0, special_allowance: emp.special_allowance || 0,
        pf_employee: emp.pf_employee || 0, income_tax: emp.income_tax || 0,
        professional_tax: emp.professional_tax || 0,
      });
    }
    setEditDialog(emp);
  };

  if (loading) return <div className="text-center py-12 text-[#64748B]">Loading...</div>;
  if (!runStatus) return null;

  const stepIcons = {
    generate: Play, attendance: CalendarCheck, joinees_exits: UserPlus,
    bonus_revisions: Money, reimbursements: Receipt, review_lock: Lock,
  };

  const employees = payrollData?.employees || [];
  const summary = payrollData?.summary || {};

  return (
    <div className="space-y-6" data-testid="payroll-wizard">
      {/* Progress Bar */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-[#0F4C81]">Run Payroll</h3>
          <span className="text-sm text-[#64748B]">{runStatus.completed_steps} of {runStatus.total_steps} steps completed</span>
        </div>
        <div className="w-full bg-[#E2E8F0] rounded-full h-2 mb-4">
          <div className="bg-[#0F4C81] h-2 rounded-full transition-all" style={{ width: `${(runStatus.completed_steps / runStatus.total_steps) * 100}%` }} data-testid="progress-bar" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {runStatus.steps.map((step) => {
            const Icon = stepIcons[step.key] || CheckCircle;
            const isActive = activeStep === step.key;
            return (
              <button key={step.key} onClick={() => setActiveStep(step.key)} data-testid={`step-${step.key}`}
                className={`flex items-start gap-3 p-3 rounded-lg border transition-all text-left ${step.done ? 'bg-green-50 border-green-200' : isActive ? 'bg-[#0F4C81]/5 border-[#0F4C81]' : 'bg-white border-[#E2E8F0] hover:border-[#0F4C81]/40'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${step.done ? 'bg-green-500 text-white' : isActive ? 'bg-[#0F4C81] text-white' : 'bg-[#E2E8F0] text-[#64748B]'}`}>
                  {step.done ? <CheckCircle size={16} weight="bold" /> : <Icon size={16} />}
                </div>
                <div className="min-w-0">
                  <p className={`text-xs font-semibold ${step.done ? 'text-green-700' : 'text-[#0F4C81]'}`}>{step.label}</p>
                  <p className="text-[10px] text-[#64748B] mt-0.5 line-clamp-2">{step.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      {activeStep === 'generate' && (
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-6" data-testid="step-generate-content">
          {runStatus.employee_count > 0 ? (
            <div className="text-center">
              <CheckCircle size={40} className="mx-auto text-green-500 mb-3" weight="fill" />
              <p className="font-semibold text-[#0F4C81]">Payroll generated with {runStatus.employee_count} employees</p>
              <p className="text-sm text-[#64748B] mt-1">Data loaded successfully</p>
              <Button className="mt-4" onClick={() => setActiveStep('attendance')} data-testid="next-step-btn">
                Next: Attendance & Payable Units <CaretRight size={14} className="ml-1" />
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="text-center">
                <Play size={40} className="mx-auto text-[#0F4C81] mb-3" />
                <p className="font-semibold text-[#0F4C81] text-lg">Generate Payroll for {MONTHS[parseInt(month.split('-')[1]) - 1]} {month.split('-')[0]}</p>
                <p className="text-sm text-[#64748B] mt-1">Choose how to load payroll data</p>
              </div>

              {/* Generate Payroll from previous month (or fresh) */}
              <div className="border border-[#E2E8F0] rounded-lg p-6 text-center bg-[#F8FAFC]/40">
                <h4 className="font-semibold text-[#0F4C81] mb-1">Generate Payroll</h4>
                <p className="text-sm text-[#64748B] mb-4">Clone salary data from the most recent month for all active employees (new joiners will be auto-added).</p>
                <Button onClick={handleGenerate} disabled={generating} className="bg-[#0F4C81] hover:bg-[#0F4C81] text-white" data-testid="generate-payroll-btn">
                  {generating ? 'Generating...' : 'Generate Payroll'}
                </Button>
              </div>

              {/* Upload Keka payroll Excel (a single file can hold multiple months) */}
              <div className="border border-[#E2E8F0] rounded-lg p-6 text-center bg-[#F8FAFC]/40">
                <h4 className="font-semibold text-[#0F4C81] mb-1">Upload Keka Payroll Excel</h4>
                <p className="text-sm text-[#64748B] mb-4">Import the exact salary figures from a Keka export. One file may contain several months — each row is filed under its own payroll month and overwrites any existing data for that month.</p>
                <input
                  id="payroll-excel-input"
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => { handleUploadExcel(e.target.files[0]); e.target.value = ''; }}
                  data-testid="payroll-excel-input"
                />
                <Button
                  onClick={() => document.getElementById('payroll-excel-input').click()}
                  disabled={uploadingExcel}
                  variant="outline"
                  className="border-[#0F4C81] text-[#0F4C81]"
                  data-testid="upload-payroll-excel-btn"
                >
                  {uploadingExcel ? 'Uploading...' : 'Upload Excel'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Attendance / Joinees / Bonus / Reimbursements steps — each with STEP-SPECIFIC columns */}
      {['attendance', 'joinees_exits', 'bonus_revisions', 'reimbursements'].includes(activeStep) && (
        <div className="space-y-4">
          {/* Step-specific summary cards */}
          {activeStep === 'attendance' && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="stats-card"><p className="text-xs text-[#64748B]">Employees</p><p className="text-2xl font-bold">{employees.length}</p></div>
              <div className="stats-card"><p className="text-xs text-[#64748B]">Days in Month</p><p className="text-2xl font-bold">{employees[0]?.payable_units?.split('/')[1]?.replace(' Days','') || 31}</p></div>
              <div className="stats-card"><p className="text-xs text-[#64748B]">Full Attendance</p><p className="text-2xl font-bold text-green-600">{employees.filter(e => { const p = e.payable_units || ''; const parts = p.split('/'); return parts[0] === parts[1]?.replace(' Days',''); }).length}</p></div>
              <div className="stats-card"><p className="text-xs text-[#64748B]">LOP / Short Days</p><p className="text-2xl font-bold text-amber-600">{employees.filter(e => { const p = e.payable_units || ''; const parts = p.split('/'); return parts[0] !== parts[1]?.replace(' Days',''); }).length}</p></div>
            </div>
          )}
          {activeStep === 'joinees_exits' && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="stats-card"><p className="text-xs text-[#64748B]">Total in Payroll</p><p className="text-2xl font-bold">{employees.length}</p></div>
              <div className="stats-card"><p className="text-xs text-[#64748B]">Departments</p><p className="text-2xl font-bold text-blue-600">{[...new Set(employees.map(e => e.department))].length}</p></div>
              <div className="stats-card"><p className="text-xs text-[#64748B]">Net Pay</p><p className="text-2xl font-bold text-green-600">{fmt(summary.total_net_pay || 0)}</p></div>
              <div className="stats-card"><p className="text-xs text-[#64748B]">Avg Gross</p><p className="text-2xl font-bold">{fmt(employees.length ? employees.reduce((s, e) => s + (e.gross || 0), 0) / employees.length : 0)}</p></div>
            </div>
          )}
          {activeStep === 'bonus_revisions' && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="stats-card"><p className="text-xs text-[#64748B]">Employees</p><p className="text-2xl font-bold">{employees.length}</p></div>
              <div className="stats-card"><p className="text-xs text-[#64748B]">Total Overtime</p><p className="text-2xl font-bold text-blue-600">{fmt(employees.reduce((s, e) => s + (e.overtime || 0), 0))}</p></div>
              <div className="stats-card"><p className="text-xs text-[#64748B]">Performance Incentives</p><p className="text-2xl font-bold text-purple-600">{fmt(employees.reduce((s, e) => s + (e.performance_incentives || 0), 0))}</p></div>
              <div className="stats-card"><p className="text-xs text-[#64748B]">Total Gross</p><p className="text-2xl font-bold text-green-600">{fmt(employees.reduce((s, e) => s + (e.gross || 0), 0))}</p></div>
            </div>
          )}
          {activeStep === 'reimbursements' && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="stats-card"><p className="text-xs text-[#64748B]">Total PF</p><p className="text-2xl font-bold">{fmt(employees.reduce((s, e) => s + (e.pf_employee || 0), 0))}</p></div>
              <div className="stats-card"><p className="text-xs text-[#64748B]">Total ESI</p><p className="text-2xl font-bold text-blue-600">{fmt(employees.reduce((s, e) => s + (e.esi_employee || 0), 0))}</p></div>
              <div className="stats-card"><p className="text-xs text-[#64748B]">Total Income Tax</p><p className="text-2xl font-bold text-amber-600">{fmt(employees.reduce((s, e) => s + (e.income_tax || 0), 0))}</p></div>
              <div className="stats-card"><p className="text-xs text-[#64748B]">Total Reimbursements</p><p className="text-2xl font-bold text-green-600">{fmt(employees.reduce((s, e) => s + (e.food_coupons || 0) + (e.travel_reimbursement || 0) + (e.fuel_reimbursement || 0), 0))}</p></div>
            </div>
          )}

          {/* Step-specific hint */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-800">
            {activeStep === 'attendance' && 'Review payable units (working days). Click edit to adjust LOP/partial days for individual employees.'}
            {activeStep === 'joinees_exits' && 'Verify all active employees are included. Use the remove button to exclude exited employees.'}
            {activeStep === 'bonus_revisions' && 'Review and edit overtime, performance incentives, and salary components for this month.'}
            {activeStep === 'reimbursements' && 'Review PF, ESI, income tax, professional tax, and reimbursement amounts before locking.'}
          </div>

          {/* STEP-SPECIFIC Employee Tables */}
          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payroll-employee-table">
                <thead className="bg-[#F8FAFC]">
                  {/* ATTENDANCE step columns */}
                  {activeStep === 'attendance' && (
                    <tr>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-[#0F4C81]">Employee</th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-[#0F4C81]">Payable Days</th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">Basic</th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">HRA</th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">Gross</th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">Net Pay</th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-[#0F4C81]">Edit</th>
                    </tr>
                  )}
                  {/* JOINEES & EXITS step columns */}
                  {activeStep === 'joinees_exits' && (
                    <tr>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-[#0F4C81]">Employee</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-[#0F4C81]">Department</th>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-[#0F4C81]">Location</th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">Gross</th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">Net Pay</th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-[#0F4C81]">Actions</th>
                    </tr>
                  )}
                  {/* BONUS & REVISIONS step columns */}
                  {activeStep === 'bonus_revisions' && (
                    <tr>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-[#0F4C81]">Employee</th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">Basic</th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">Special Allow.</th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">Overtime</th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">Perf. Incentive</th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">Gross</th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-[#0F4C81]">Edit</th>
                    </tr>
                  )}
                  {/* REIMBURSEMENTS & DEDUCTIONS step columns */}
                  {activeStep === 'reimbursements' && (
                    <tr>
                      <th className="px-3 py-2.5 text-left text-xs font-semibold text-[#0F4C81]">Employee</th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">Food</th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">Travel</th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">Fuel</th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">PF</th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">ESI</th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">Tax</th>
                      <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">Prof. Tax</th>
                      <th className="px-3 py-2.5 text-center text-xs font-semibold text-[#0F4C81]">Edit</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {employees.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-8 text-[#64748B]">No employees. Generate payroll first.</td></tr>
                  ) : employees.map(emp => (
                    <tr key={emp.employee_number} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`payroll-row-${emp.employee_number}`}>
                      {/* Employee name - always first column */}
                      <td className="px-3 py-2.5">
                        <p className="font-medium text-sm">{emp.employee_name}</p>
                        <p className="text-[10px] text-[#64748B]">{emp.employee_number}</p>
                      </td>

                      {/* ATTENDANCE step cells */}
                      {activeStep === 'attendance' && <>
                        <td className="px-3 py-2.5 text-center">
                          <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${(() => { const p = emp.payable_units || ''; const parts = p.split('/'); return parts[0] === parts[1]?.replace(' Days','') ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'; })()}`}>
                            {emp.payable_units || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-right text-sm">{fmt(emp.basic)}</td>
                        <td className="px-3 py-2.5 text-right text-sm">{fmt(emp.hra)}</td>
                        <td className="px-3 py-2.5 text-right text-sm font-medium">{fmt(emp.gross)}</td>
                        <td className="px-3 py-2.5 text-right text-sm font-semibold text-green-700">{fmt(emp.total_net_pay)}</td>
                        <td className="px-3 py-2.5 text-center">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(emp)}><PencilSimple size={14} className="text-[#0F4C81]" /></Button>
                        </td>
                      </>}

                      {/* JOINEES & EXITS step cells */}
                      {activeStep === 'joinees_exits' && <>
                        <td className="px-3 py-2.5 text-sm">{emp.department || '—'}</td>
                        <td className="px-3 py-2.5 text-sm">{emp.location || '—'}</td>
                        <td className="px-3 py-2.5 text-right text-sm font-medium">{fmt(emp.gross)}</td>
                        <td className="px-3 py-2.5 text-right text-sm font-semibold text-green-700">{fmt(emp.total_net_pay)}</td>
                        <td className="px-3 py-2.5 text-center">
                          <Button variant="ghost" size="sm" className="h-7 p-0 px-2 text-red-500 hover:bg-red-50" onClick={() => handleRemoveEmployee(emp.employee_number)} data-testid={`remove-${emp.employee_number}`}>
                            <Trash size={14} className="mr-1" /> Remove
                          </Button>
                        </td>
                      </>}

                      {/* BONUS & REVISIONS step cells */}
                      {activeStep === 'bonus_revisions' && <>
                        <td className="px-3 py-2.5 text-right text-sm">{fmt(emp.basic)}</td>
                        <td className="px-3 py-2.5 text-right text-sm">{fmt(emp.special_allowance)}</td>
                        <td className="px-3 py-2.5 text-right text-sm font-medium text-blue-600">{fmt(emp.overtime)}</td>
                        <td className="px-3 py-2.5 text-right text-sm font-medium text-purple-600">{fmt(emp.performance_incentives)}</td>
                        <td className="px-3 py-2.5 text-right text-sm font-semibold">{fmt(emp.gross)}</td>
                        <td className="px-3 py-2.5 text-center">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(emp)}><PencilSimple size={14} className="text-[#0F4C81]" /></Button>
                        </td>
                      </>}

                      {/* REIMBURSEMENTS & DEDUCTIONS step cells */}
                      {activeStep === 'reimbursements' && <>
                        <td className="px-3 py-2.5 text-right text-sm">{fmt(emp.food_coupons)}</td>
                        <td className="px-3 py-2.5 text-right text-sm">{fmt(emp.travel_reimbursement)}</td>
                        <td className="px-3 py-2.5 text-right text-sm">{fmt(emp.fuel_reimbursement)}</td>
                        <td className="px-3 py-2.5 text-right text-sm text-red-600">{fmt(emp.pf_employee)}</td>
                        <td className="px-3 py-2.5 text-right text-sm text-red-600">{fmt(emp.esi_employee)}</td>
                        <td className="px-3 py-2.5 text-right text-sm text-red-600">{fmt(emp.income_tax)}</td>
                        <td className="px-3 py-2.5 text-right text-sm text-red-600">{fmt(emp.professional_tax)}</td>
                        <td className="px-3 py-2.5 text-center">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(emp)}><PencilSimple size={14} className="text-[#0F4C81]" /></Button>
                        </td>
                      </>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Step Actions */}
          <div className="flex justify-between items-center">
            <p className="text-xs text-[#64748B]">{employees.length} employees</p>
            <div className="flex gap-2">
              {runStatus.steps.find(s => s.key === activeStep)?.done ? (
                <span className="flex items-center gap-1.5 text-green-600 text-sm font-medium"><CheckCircle size={16} weight="fill" /> Completed</span>
              ) : (
                <Button onClick={() => handleStepComplete(activeStep)} className="bg-[#0F4C81] hover:bg-[#0F4C81]" data-testid="mark-step-done-btn">
                  <CheckCircle size={14} className="mr-1" /> Mark Step Complete
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Review & Lock step */}
      {activeStep === 'review_lock' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="stats-card"><p className="text-xs text-[#64748B]">Total Employees</p><p className="text-2xl font-bold">{summary.total_employees || 0}</p></div>
            <div className="stats-card"><p className="text-xs text-[#64748B]">Net Pay</p><p className="text-2xl font-bold">{fmt(summary.total_net_pay)}</p></div>
            <div className="stats-card bg-amber-50 border-amber-200"><p className="text-xs text-amber-600">Ad-hoc Amounts</p><p className="text-2xl font-bold text-amber-700">{fmt(summary.total_adhoc)}</p></div>
            <div className="stats-card bg-green-50 border-green-200"><p className="text-xs text-green-600">Total Disbursal</p><p className="text-2xl font-bold text-green-700">{fmt(summary.total_disbursal)}</p></div>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 text-center" data-testid="review-lock-content">
            {runStatus.is_locked ? (
              <>
                <Lock size={40} className="mx-auto text-green-500 mb-3" weight="fill" />
                <p className="font-semibold text-green-700">Payroll is locked</p>
                <p className="text-sm text-[#64748B] mt-1">Ready for bank disbursal. Download the salary sheet.</p>
              </>
            ) : (
              <>
                <Eye size={40} className="mx-auto text-[#0F4C81] mb-3" />
                <p className="font-semibold text-[#0F4C81]">Review complete? Lock payroll for disbursal</p>
                <p className="text-sm text-[#64748B] mt-1 mb-4">Once locked, salary data cannot be modified until unlocked.</p>
                <Button onClick={handleLock} className="bg-[#0F4C81] hover:bg-[#0B3C68]" data-testid="lock-payroll-btn">
                  <Lock size={14} className="mr-1" /> Lock Payroll
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Edit Employee Dialog — Step-specific fields */}
      <Dialog open={!!editDialog} onOpenChange={() => setEditDialog(null)}>
        <DialogContent className="max-w-md" data-testid="edit-payroll-dialog">
          <DialogHeader>
            <DialogTitle>Edit — {editDialog?.employee_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {activeStep === 'attendance' ? (
              <div className="flex items-center gap-3">
                <label className="text-xs text-[#64748B] w-32">Payable Days</label>
                <Input value={editValues.payable_units ?? ''} onChange={(e) => setEditValues(v => ({...v, payable_units: e.target.value}))} placeholder="e.g. 25/31 Days" data-testid="edit-payable-units" />
              </div>
            ) : activeStep === 'bonus_revisions' ? (
              <>
                {[['basic', 'Basic'], ['special_allowance', 'Special Allowance'], ['overtime', 'Overtime'], ['performance_incentives', 'Perf. Incentive']].map(([key, label]) => (
                  <div key={key} className="flex items-center gap-3">
                    <label className="text-xs text-[#64748B] w-32">{label}</label>
                    <Input type="number" className="flex-1" value={editValues[key] ?? ''} onChange={(e) => setEditValues(v => ({...v, [key]: e.target.value}))} data-testid={`edit-${key}`} />
                  </div>
                ))}
              </>
            ) : activeStep === 'reimbursements' ? (
              <>
                {[['food_coupons', 'Food Coupons'], ['travel_reimbursement', 'Travel'], ['fuel_reimbursement', 'Fuel'], ['pf_employee', 'PF (Employee)'], ['esi_employee', 'ESI (Employee)'], ['income_tax', 'Income Tax'], ['professional_tax', 'Prof. Tax']].map(([key, label]) => (
                  <div key={key} className="flex items-center gap-3">
                    <label className="text-xs text-[#64748B] w-32">{label}</label>
                    <Input type="number" className="flex-1" value={editValues[key] ?? ''} onChange={(e) => setEditValues(v => ({...v, [key]: e.target.value}))} data-testid={`edit-${key}`} />
                  </div>
                ))}
              </>
            ) : (
              <>
                {[['basic', 'Basic'], ['hra', 'HRA'], ['special_allowance', 'Special Allowance'], ['pf_employee', 'PF (Employee)'], ['income_tax', 'Income Tax'], ['professional_tax', 'Prof. Tax']].map(([key, label]) => (
                  <div key={key} className="flex items-center gap-3">
                    <label className="text-xs text-[#64748B] w-32">{label}</label>
                    <Input type="number" className="flex-1" value={editValues[key] ?? ''} onChange={(e) => setEditValues(v => ({...v, [key]: e.target.value}))} data-testid={`edit-${key}`} />
                  </div>
                ))}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)}>Cancel</Button>
            <Button onClick={handleEditSave} data-testid="save-edit-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ─── Main Payroll Page ───────────────────────────────────────────────────────
export default function PayrollPage() {
  const now = new Date();
  const [year, setYear] = useState(String(now.getFullYear()));
  const [month, setMonth] = useState(String(now.getMonth() + 1).padStart(2, '0'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [adhocDialog, setAdhocDialog] = useState(false);
  const [adhocForm, setAdhocForm] = useState({ employee_number: '', amount: '', label: 'Bonus' });
  const [availableMonths, setAvailableMonths] = useState([]);
  const [initialized, setInitialized] = useState(false);
  const [viewMode, setViewMode] = useState('auto'); // auto, wizard, table
  const [uploadingExcel, setUploadingExcel] = useState(false);

  const selectedMonth = `${year}-${month}`;
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    payrollAPI.getAvailableMonths().then(res => {
      const months = res.data.months || [];
      setAvailableMonths(months);
      // Default to current month (so user can create it), or latest available
      const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const hasCurrentMonth = months.some(m => m.value === currentMonth);
      if (!hasCurrentMonth && months.length > 0) {
        // Default to current month to allow creation, but show wizard
      }
      setInitialized(true);
    }).catch(() => setInitialized(true));
  }, []);

  const load = useCallback(async () => {
    if (!initialized) return;
    setLoading(true);
    try {
      const res = await payrollAPI.getMonthly(selectedMonth);
      setData(res.data);
      if (res.data.employees?.length > 0) setViewMode('table');
    } catch (e) {
      setData(null);
      setViewMode('wizard');
    } finally { setLoading(false); }
  }, [selectedMonth, initialized]);

  useEffect(() => { load(); }, [load]);

  const handleUnlock = async () => {
    try { await payrollAPI.unlockPayroll(selectedMonth); toast.success('Unlocked'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleLock = async () => {
    try { await payrollAPI.lockPayroll(selectedMonth); toast.success('Locked'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleAddAdhoc = async () => {
    try {
      await payrollAPI.addAdhoc({ month: selectedMonth, ...adhocForm, amount: parseFloat(adhocForm.amount) });
      toast.success('Ad-hoc added'); setAdhocDialog(false); setAdhocForm({ employee_number: '', amount: '', label: 'Bonus' }); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleToolbarUpload = async (file) => {
    if (!file) return;
    setUploadingExcel(true);
    try {
      const res = await payrollAPI.uploadPayrollExcel(selectedMonth, file);
      toast.success(res.data.message || 'Payroll uploaded');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to upload payroll Excel'); }
    finally { setUploadingExcel(false); }
  };

  const hasData = data && data.employees?.length > 0;
  const showWizard = !hasData || viewMode === 'wizard';

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <PageHeader title="Payroll Management" subtitle="Run payroll step-by-step, lock and generate bank salary sheet" />

      {/* Month Selector + Actions */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex flex-wrap items-center gap-3">
        <div className="flex flex-col">
          <label className="text-[10px] text-[#64748B] mb-0.5 uppercase tracking-wide">Payroll Month</label>
          <input
            type="month"
            value={`${year}-${month}`}
            min="2024-01"
            max="2027-12"
            onChange={(e) => {
              const v = e.target.value; // YYYY-MM
              if (!v) return;
              const [y, m] = v.split('-');
              setYear(y); setMonth(m); setViewMode('auto');
            }}
            data-testid="payroll-month-picker"
            className="h-9 rounded-md border border-[#E2E8F0] px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0F4C81]/30"
          />
        </div>

        {hasData && (
          <>
            {data.is_locked ? (
              <Button variant="outline" size="sm" onClick={handleUnlock} data-testid="unlock-btn"><LockOpen size={14} className="mr-1" /> Unlock</Button>
            ) : (
              <Button variant="outline" size="sm" onClick={handleLock} data-testid="lock-btn"><Lock size={14} className="mr-1" /> Lock Payroll</Button>
            )}
            <Button variant="outline" size="sm" className="bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100" onClick={() => setAdhocDialog(true)} data-testid="adhoc-btn">
              <Plus size={14} className="mr-1" /> Ad-hoc Amount
            </Button>
            <input id="payroll-excel-toolbar-input" type="file" accept=".xlsx,.xls" className="hidden"
              onChange={(e) => { handleToolbarUpload(e.target.files[0]); e.target.value = ''; }} data-testid="payroll-excel-toolbar-input" />
            <Button variant="outline" size="sm" disabled={uploadingExcel}
              onClick={() => document.getElementById('payroll-excel-toolbar-input').click()} data-testid="upload-payroll-excel-toolbar-btn">
              <DownloadSimple size={14} className="mr-1 rotate-180" /> {uploadingExcel ? 'Uploading...' : 'Upload Excel'}
            </Button>
          </>
        )}

        <div className="flex gap-1 ml-auto">
          {hasData && (
            <>
              <Button variant={viewMode === 'wizard' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('wizard')} data-testid="view-wizard-btn">
                <Play size={14} className="mr-1" /> Run Steps
              </Button>
              <Button variant={viewMode === 'table' ? 'default' : 'outline'} size="sm" onClick={() => setViewMode('table')} data-testid="view-table-btn">
                <Eye size={14} className="mr-1" /> Table View
              </Button>
            </>
          )}
          {hasData && (
            <a href={`${API_URL}/api/hr/payroll/salary-sheet?month=${selectedMonth}`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" data-testid="download-sheet-btn"><DownloadSimple size={14} className="mr-1" /> Salary Sheet</Button>
            </a>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#64748B]">Loading...</div>
      ) : showWizard ? (
        <PayrollWizard month={selectedMonth} onComplete={load} />
      ) : (
        /* Table View — existing payroll display */
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="stats-card" data-testid="stat-employees">
              <div className="flex items-center gap-2 mb-1"><Users size={16} className="text-[#0F4C81]" /><p className="text-xs text-[#64748B]">Employees</p></div>
              <p className="text-2xl font-bold">{data.summary?.total_employees || 0}</p>
            </div>
            <div className="stats-card" data-testid="stat-netpay">
              <div className="flex items-center gap-2 mb-1"><Money size={16} className="text-[#0F4C81]" /><p className="text-xs text-[#64748B]">Net Pay (Salary)</p></div>
              <p className="text-2xl font-bold">{fmt(data.summary?.total_net_pay)}</p>
            </div>
            <div className="stats-card bg-blue-50 border-blue-200" data-testid="stat-incentive">
              <div className="flex items-center gap-2 mb-1"><Money size={16} className="text-blue-600" /><p className="text-xs text-blue-600">Incentives</p></div>
              <p className="text-2xl font-bold text-blue-700">{fmt(data.summary?.total_incentive)}</p>
            </div>
            <div className="stats-card bg-amber-50 border-amber-200" data-testid="stat-adhoc">
              <div className="flex items-center gap-2 mb-1"><Plus size={16} className="text-amber-600" /><p className="text-xs text-amber-600">Ad-hoc Amounts</p></div>
              <p className="text-2xl font-bold text-amber-700">{fmt(data.summary?.total_adhoc)}</p>
            </div>
            <div className="stats-card bg-green-50 border-green-200" data-testid="stat-disbursal">
              <div className="flex items-center gap-2 mb-1"><CheckCircle size={16} className="text-green-600" /><p className="text-xs text-green-600">Total Disbursal</p></div>
              <p className="text-2xl font-bold text-green-700">{fmt(data.summary?.total_disbursal)}</p>
            </div>
          </div>

          {/* Lock Status */}
          {data.is_locked && (
            <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-center gap-2 text-green-700 text-sm" data-testid="locked-banner">
              <Lock size={16} weight="fill" /> Payroll locked on {new Date(data.locked_at).toLocaleString('en-IN')}
            </div>
          )}

          {/* Employee Table */}
          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="payroll-table">
                <thead className="bg-[#F8FAFC]">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-[#0F4C81]">Employee</th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">Basic</th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">HRA</th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">Gross (Salary)</th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-blue-700">Incentive</th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">Deductions</th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">Net Pay</th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">Ad-hoc</th>
                    <th className="px-3 py-2.5 text-right text-xs font-semibold text-[#0F4C81]">Disbursal</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.employees || []).map(emp => (
                    <tr key={emp.employee_number} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`table-row-${emp.employee_number}`}>
                      <td className="px-3 py-2.5">
                        <p className="font-medium text-sm">{emp.employee_name}</p>
                        <p className="text-[10px] text-[#64748B]">{emp.employee_number} — {emp.department}</p>
                      </td>
                      <td className="px-3 py-2.5 text-right text-sm">{fmt(emp.basic)}</td>
                      <td className="px-3 py-2.5 text-right text-sm">{fmt(emp.hra)}</td>
                      <td className="px-3 py-2.5 text-right text-sm font-medium">{fmt(emp.salary_gross)}</td>
                      <td className="px-3 py-2.5 text-right text-sm font-medium text-blue-700">{emp.performance_incentives > 0 ? fmt(emp.performance_incentives) : '—'}</td>
                      <td className="px-3 py-2.5 text-right text-sm text-red-600">{fmt(emp.total_deductions)}</td>
                      <td className="px-3 py-2.5 text-right text-sm font-semibold text-[#0F4C81]">{fmt(emp.total_net_pay)}</td>
                      <td className="px-3 py-2.5 text-right text-sm text-amber-600">{emp.adhoc_total ? fmt(emp.adhoc_total) : '—'}</td>
                      <td className="px-3 py-2.5 text-right text-sm font-bold text-green-700">{fmt(emp.disbursal_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Add Ad-hoc Dialog */}
      <Dialog open={adhocDialog} onOpenChange={setAdhocDialog}>
        <DialogContent className="max-w-sm" data-testid="adhoc-dialog">
          <DialogHeader><DialogTitle>Add Ad-hoc Amount</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-[#64748B]">Employee Number</label>
              <Input value={adhocForm.employee_number} onChange={(e) => setAdhocForm(f => ({...f, employee_number: e.target.value.toUpperCase()}))} placeholder="GOI0063" data-testid="adhoc-emp" />
            </div>
            <div>
              <label className="text-xs text-[#64748B]">Amount (INR)</label>
              <Input type="number" value={adhocForm.amount} onChange={(e) => setAdhocForm(f => ({...f, amount: e.target.value}))} data-testid="adhoc-amount" />
            </div>
            <div>
              <label className="text-xs text-[#64748B]">Label</label>
              <Select value={adhocForm.label} onValueChange={(v) => setAdhocForm(f => ({...f, label: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Bonus">Bonus</SelectItem>
                  <SelectItem value="Incentive">Incentive</SelectItem>
                  <SelectItem value="Arrears">Arrears</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdhocDialog(false)}>Cancel</Button>
            <Button onClick={handleAddAdhoc} disabled={!adhocForm.employee_number || !adhocForm.amount} data-testid="add-adhoc-btn">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
